/**
 * NotesConfig - Configuration for NotesApp_v2
 * Simplified flow-based document for quick note-taking
 */

import { AppConfig, SupportedClass } from '../../../services/baseApplication/types';

/**
 * Notes-specific supported classes
 * Simplified subset of DocsApp classes for quick note-taking
 */
export const NOTES_SUPPORTED_CLASSES: Record<string, SupportedClass> = {
  // Text Elements
  'note-title': {
    displayName: 'Note Title',
    description: 'Main note title',
    category: 'heading',
    defaultAttrs: {
      position: { type: 'flow' },
      style: { fontSize: 32, fontWeight: 600 }
    }
  },

  'note-text': {
    displayName: 'Text',
    description: 'Plain text content',
    category: 'text',
    defaultAttrs: {
      position: { type: 'flow' },
      style: { fontSize: 16, lineHeight: 1.6 }
    }
  },

  // Lists
  'note-bullet-list': {
    displayName: 'Bullet List',
    description: 'Bulleted list',
    category: 'list',
    defaultAttrs: {
      position: { type: 'flow' }
    }
  },

  'note-numbered-list': {
    displayName: 'Numbered List',
    description: 'Numbered list',
    category: 'list',
    defaultAttrs: {
      position: { type: 'flow' }
    }
  },

  'note-checklist': {
    displayName: 'Checklist',
    description: 'Todo checklist',
    category: 'list',
    defaultAttrs: {
      position: { type: 'flow' },
      semantic: { checked: false }
    }
  },

  // Special
  'note-divider': {
    displayName: 'Divider',
    description: 'Horizontal divider line',
    category: 'structural',
    defaultAttrs: {
      position: { type: 'flow' },
      style: { borderTop: '1px solid #e0e0e0' }
    }
  },

  'note-quote': {
    displayName: 'Quote',
    description: 'Blockquote',
    category: 'quote',
    defaultAttrs: {
      position: { type: 'flow' },
      style: { borderLeft: '4px solid #1976d2', paddingLeft: 16, color: '#666' }
    }
  },

  'note-code': {
    displayName: 'Code Block',
    description: 'Code snippet',
    category: 'code',
    defaultAttrs: {
      position: { type: 'flow' },
      style: { fontFamily: 'monospace', background: '#f5f5f5', padding: 12 }
    }
  },

  // Media (simplified)
  'note-image': {
    displayName: 'Image',
    description: 'Embedded image',
    category: 'media',
    defaultAttrs: {
      position: { type: 'flow' },
      semantic: { src: '', alt: '' }
    }
  },

  'note-link': {
    displayName: 'Link',
    description: 'Hyperlink',
    category: 'interactive',
    defaultAttrs: {
      position: { type: 'flow' },
      semantic: { url: '', displayText: '' }
    }
  },

  // Tags & Metadata
  'note-tag': {
    displayName: 'Tag',
    description: 'Note tag/label',
    category: 'metadata',
    defaultAttrs: {
      position: { type: 'flow' },
      semantic: { tag: '' },
      style: { background: '#e3f2fd', color: '#1976d2', padding: '4px 8px', borderRadius: 4 }
    }
  }
};

/**
 * Notes Application Configuration
 */
export const NOTES_APP_CONFIG: AppConfig = {
  appId: 'notes_v2',
  appName: 'Notes v2',
  containerType: 'note',
  layoutMode: 'flow',
  supportedClasses: NOTES_SUPPORTED_CLASSES,
  supportedPositionTypes: ['flow'],
  supportsDragDrop: false,
  supportsResize: false,
  supportsRotation: false,
  defaultContainer: {
    width: undefined, // Flow-based
    height: undefined,
    infinite: false
  },
  
  editorSelector: '.notes-app-v2' // ✅ Required by AppConfig
};

/**
 * Helper: Get supported className list
 */
export function getSupportedClassNames(): string[] {
  return Object.keys(NOTES_SUPPORTED_CLASSES);
}

/**
 * Helper: Check if className is supported
 */
export function isClassNameSupported(className: string): boolean {
  return className in NOTES_SUPPORTED_CLASSES;
}

/**
 * Helper: Get class info
 */
export function getClassInfo(className: string): SupportedClass | undefined {
  return NOTES_SUPPORTED_CLASSES[className];
}

/**
 * Helper: Check if className is a note element
 */
export function isNoteClassName(className: string): boolean {
  return className.startsWith('note-');
}

